import { HsaEligibility } from "@redotech/redo-conversion-hsa-common/hsa-eligibility";
import { useEffect, useRef, useState } from "react";
import { StorefrontClient } from "../../client/storefront";
import { useCart } from "../../utils/cart";
import {
  eligibilityFromMetafields,
  HSA_METAFIELD_IDENTIFIERS,
  HsaCartEligibilitySummary,
  isRecurringCartItem,
  summarizeHsaCart,
} from "./hsa-cart-summary";
import { isUnitedStates } from "./hsa-country-gate";

/**
 * Resolves aggregate HSA/FSA eligibility for the whole cart: cart lines →
 * unique variant ids → ONE batched metafield query
 * (`StorefrontClient.getVariantsMetafields`), resolved per variant through the
 * shared `resolveHsaEligibilityFromMetafields`. Re-runs when the cart changes
 * (`useCart` watches cart requests, cart-app events, and bfcache restores).
 * Undefined off-US, while the cart or a lookup is loading, or on error.
 */
export function useHsaCartEligibility({
  storefrontClient,
}: {
  storefrontClient: StorefrontClient;
}): HsaCartEligibilitySummary | undefined {
  const cart = useCart();
  const [summary, setSummary] = useState<HsaCartEligibilitySummary | undefined>(
    undefined,
  );
  // Per-variant resolution cache so cart edits only look up new variants.
  const cacheRef = useRef(new Map<string, HsaEligibility>());

  const lineKey = cart?.items
    .map(
      (item) =>
        `${item.variant_id}x${item.quantity}@${item.selling_plan_allocation?.selling_plan.id ?? ""}`,
    )
    .join(",");

  useEffect(() => {
    if (!cart || !isUnitedStates()) {
      setSummary(undefined);
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        const cache = cacheRef.current;
        const variantIds = [
          ...new Set(cart.items.map((item) => String(item.variant_id))),
        ];
        const unresolvedIds = variantIds.filter((id) => !cache.has(id));
        if (unresolvedIds.length) {
          const metafieldsByVariantId =
            await storefrontClient.getVariantsMetafields(
              unresolvedIds,
              HSA_METAFIELD_IDENTIFIERS,
              controller.signal,
            );
          for (const id of unresolvedIds) {
            cache.set(
              id,
              eligibilityFromMetafields(metafieldsByVariantId.get(id)),
            );
          }
        }
        if (controller.signal.aborted) {
          return;
        }

        setSummary(
          summarizeHsaCart(
            cart.items.map((item) => ({
              variantId: String(item.variant_id),
              quantity: item.quantity,
              recurring: isRecurringCartItem(item),
            })),
            cache,
          ),
        );
      } catch {
        if (!controller.signal.aborted) {
          setSummary(undefined);
        }
      }
    })();
    return () => controller.abort();
    // lineKey captures the cart contents the summary derives from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineKey, storefrontClient]);

  return summary;
}
